// Customise drawer body. Mirrors widgetRegistry section-by-section,
// renders a switch per widget that calls dismiss/restore on the page's
// useWidgetVisibility hook. Templates (compact / risk / etc.) apply
// hidden-id sets in bulk.
//
// Postgres mapping: per-user hidden ids ultimately persist in
// `user_preferences`; templates could become a `tenancy_widget_templates`
// table if customers want branded defaults.
// Integration hook: see useWidgetVisibility.js.
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Popover, Typography, IconButton, Box } from '@mui/material';
import LoadingSwitch from '../../../components/LoadingSwitch';
import { useTheme } from '@mui/material/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { TEMPLATES, WIDGET_SECTIONS } from '../widgetRegistry';
import { FEATURES, subscribeFeatures } from '../featureFlags';

const PANEL_CSS = `
.rbx-tmpl-btn {
    font-size: 11px;
    font-weight: 500;
    padding: 4px 10px;
    border-radius: 6px;
    border: 1px solid var(--border2);
    background: transparent;
    color: var(--muted);
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    transition: all 0.12s;
    white-space: nowrap;
    line-height: 1.4;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.rbx-tmpl-btn:hover { border-color: var(--teal); color: var(--teal); }
.rbx-tmpl-btn.active { background: var(--teal); border-color: var(--teal); color: #fff; }
.rbx-tmpl-custom {
    display: inline-flex; align-items: center; gap: 0;
    border: 1px solid var(--border2); border-radius: 6px; overflow: hidden;
    transition: border-color 0.12s;
}
.rbx-tmpl-custom:hover { border-color: var(--teal); }
.rbx-tmpl-custom.active { border-color: var(--teal); background: var(--teal); }
.rbx-tmpl-custom-label {
    font-size: 11px; font-weight: 500; padding: 4px 8px;
    background: transparent; color: var(--muted); cursor: pointer;
    font-family: 'Inter', sans-serif; border: none;
    transition: color 0.12s; white-space: nowrap; line-height: 1.4;
    text-transform: uppercase; letter-spacing: 0.04em;
}
.rbx-tmpl-custom.active .rbx-tmpl-custom-label { color: #fff; }
.rbx-tmpl-custom-del {
    font-size: 9px; padding: 4px 6px 4px 2px; background: transparent;
    color: var(--dim); cursor: pointer; border: none; font-family: 'Inter', sans-serif;
    line-height: 1; transition: color 0.12s;
}
.rbx-tmpl-custom-del:hover { color: var(--red); }
.rbx-tmpl-custom.active .rbx-tmpl-custom-del { color: rgba(255,255,255,0.6); }
.rbx-tmpl-custom.active .rbx-tmpl-custom-del:hover { color: #fff; }
.rbx-save-input {
    flex: 1; font-size: 11px; padding: 5px 8px;
    border-radius: 6px; border: 1px solid var(--border2);
    background: var(--surface2); color: var(--text);
    font-family: 'Inter', sans-serif; outline: none;
    transition: border-color 0.12s; min-width: 0;
}
.rbx-save-input::placeholder { color: var(--dim); }
.rbx-save-input:focus { border-color: var(--teal); }
.rbx-save-btn {
    font-size: 11px; font-weight: 600; padding: 5px 10px;
    border-radius: 6px; border: none;
    background: var(--teal); color: #fff; cursor: pointer;
    font-family: 'Inter', sans-serif; transition: filter 0.12s;
    white-space: nowrap; flex-shrink: 0;
}
.rbx-save-btn:hover { filter: brightness(0.92); }
.rbx-save-btn:disabled { background: var(--bar-track); color: var(--dim); cursor: not-allowed; }
`;

// Widget row, label + description tooltip trigger + Switch + ON/OFF label.
// Uses MUI Switch (same component as the Features dialog) but pairs it with
// an explicit ON/OFF caption so the state is unambiguous to readers who
// don't yet have the muscle memory for toggle colours.
function WidgetRow({ widget, isVisible, onToggle, onInfoOpen, secondary }) {
    return (
        <Box
            sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.25, py: 0.75,
                borderRadius: '8px',
                transition: 'background 120ms',
                '&:hover': { bgcolor: 'action.hover' },
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                    sx={{
                        fontSize: 11, fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                        color: isVisible ? secondary : 'text.disabled',
                        lineHeight: 1.3,
                        transition: 'color 120ms',
                    }}
                >
                    {widget.label}
                </Typography>
                <IconButton
                    aria-label={`Show description for ${widget.label}`}
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onInfoOpen(e, widget); }}
                    sx={{ p: '2px', color: 'text.disabled', '&:hover': { color: 'secondary.main', background: 'transparent' } }}
                >
                    <HelpOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
            </Box>
            <LoadingSwitch
                checked={isVisible}
                onChange={() => onToggle(widget.id)}
                loading={false}
                inputProps={{ 'aria-label': `Toggle ${widget.label}` }}
            />
        </Box>
    );
}

export default function WidgetPanel({ open, onClose, allWidgets, hidden, onToggle, onRestoreAll, page = 'summary', customTemplates = [], onSaveTemplate, onDeleteTemplate }) {
    const theme = useTheme();
    const secondary = theme.palette.secondary.main;
    // Re-render when tenancy feature flags change so widgets gated by a
    // disabled feature disappear from the panel in real time.
    const [featureTick, bumpFeatures] = useState(0);
    useEffect(() => subscribeFeatures(() => bumpFeatures((n) => n + 1)), []);
    const sections = useMemo(() => {
        const raw = WIDGET_SECTIONS[page] || [];
        return raw
            .map((s) => ({ ...s, widgets: s.widgets.filter((w) => !w.feature || FEATURES[w.feature]) }))
            .filter((s) => s.widgets.length > 0);
        // featureTick is intentional, it forces recompute on flag toggle
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, featureTick]);
    const builtInTemplates = TEMPLATES[page] || [];
    const [saveName, setSaveName] = useState('');
    const [pendingDelete, setPendingDelete] = useState(null);
    const [infoPopover, setInfoPopover] = useState(null);
    const inputRef = useRef(null);

    const handleInfoOpen = (e, widget) => {
        e.stopPropagation();
        setInfoPopover((prev) =>
            prev?.widget.id === widget.id ? null : { anchorEl: e.currentTarget, widget }
        );
    };
    const handleInfoClose = () => setInfoPopover(null);

    const allTemplates = [...builtInTemplates, ...customTemplates];

    const activeTemplate = allTemplates.find((t) => {
        const tHidden = [...t.hidden].sort().join(',');
        const curHidden = [...hidden].sort().join(',');
        return tHidden === curHidden;
    });

    const applyTemplate = (tmpl) => {
        const allIds = allWidgets.map((w) => w.id);
        allIds.forEach((id) => {
            const shouldBeHidden = tmpl.hidden.includes(id);
            const isHidden = hidden.includes(id);
            if (shouldBeHidden !== isHidden) onToggle(id);
        });
    };

    const saveTemplate = () => {
        const name = saveName.trim();
        if (!name) return;
        onSaveTemplate?.(name);   // persisted on the tenancy's layout record via useWidgetVisibility
        setSaveName('');
    };

    const deleteTemplate = (id) => {
        onDeleteTemplate?.(id);
        setPendingDelete(null);
    };

    const toggleSection = (section) => {
        // If every widget in the section is visible, hide them all; otherwise
        // restore them all. Lets the user collapse an entire block in one
        // gesture, matching the per-section structure on the page.
        const ids = section.widgets.map((w) => w.id);
        const allOn = ids.every((id) => !hidden.includes(id));
        ids.forEach((id) => {
            const currentlyHidden = hidden.includes(id);
            if (allOn && !currentlyHidden) onToggle(id);          // hide all
            else if (!allOn && currentlyHidden) onToggle(id);     // show all
        });
    };

    return (
        <>
            <style>{PANEL_CSS}</style>

            {/* Backdrop */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        onClick={() => { setPendingDelete(null); onClose(); }}
                        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.08)', zIndex: 1200 }}
                    />
                )}
            </AnimatePresence>

            {/* Floating modal */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        key="panel"
                        initial={{ opacity: 0, scale: 0.97, y: -6 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.97, y: -6 }}
                        transition={{ duration: 0.18, ease: 'easeOut' }}
                        style={{
                            position: 'fixed',
                            top: 60, right: 24,
                            width: 340,
                            maxHeight: 'calc(100vh - 80px)',
                            zIndex: 1300,
                            display: 'flex',
                            flexDirection: 'column',
                            background: 'var(--surface)',
                            border: '1px solid var(--border2)',
                            borderRadius: 14,
                            boxShadow: '0 8px 40px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Header */}
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '16px 24px 12px',
                            flexShrink: 0,
                        }}>
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>
                                    Customise Layout
                                </div>
                                <div style={{ fontSize: 10, color: 'var(--dim)', marginTop: 2 }}>
                                    {allWidgets.length - hidden.length} of {allWidgets.length} widgets visible
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
                                    fontSize: 14, color: 'var(--dim)', borderRadius: 4,
                                    lineHeight: 1, fontFamily: 'inherit', transition: 'color 0.12s',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dim)'; }}
                            >
                                x
                            </button>
                        </div>

                        {/* Scrollable body */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 20px 16px' }}>

                            {/* Templates */}
                            {(builtInTemplates.length > 0 || customTemplates.length > 0) && (
                                <div style={{ marginBottom: 18 }}>
                                    <div style={{
                                        fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                                        letterSpacing: '0.07em', color: 'var(--dim)', marginBottom: 8,
                                    }}>
                                        Layouts
                                    </div>

                                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
                                        {builtInTemplates.map((t) => (
                                            <button
                                                key={t.id}
                                                className={`rbx-tmpl-btn${activeTemplate?.id === t.id ? ' active' : ''}`}
                                                onClick={() => applyTemplate(t)}
                                                title={t.description}
                                            >
                                                {t.label}
                                            </button>
                                        ))}
                                        <AnimatePresence>
                                            {customTemplates.map((t) => (
                                                <motion.div
                                                    key={t.id}
                                                    initial={{ opacity: 0, scale: 0.85 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    exit={{ opacity: 0, scale: 0.85 }}
                                                    transition={{ duration: 0.15 }}
                                                >
                                                    {pendingDelete === t.id ? (
                                                        <div style={{
                                                            display: 'flex', alignItems: 'center', gap: 6,
                                                            padding: '5px 8px',
                                                            border: '1px solid var(--red)',
                                                            borderRadius: 6,
                                                            background: 'var(--red-bg)',
                                                        }}>
                                                            <span style={{ fontSize: 11, color: 'var(--red-text)', whiteSpace: 'nowrap', fontFamily: 'Inter, sans-serif' }}>
                                                                Delete?
                                                            </span>
                                                            <button
                                                                onClick={() => deleteTemplate(t.id)}
                                                                style={{
                                                                    fontSize: 11, fontWeight: 600, padding: '3px 10px',
                                                                    borderRadius: 5, border: 'none',
                                                                    background: 'var(--red)', color: '#fff',
                                                                    cursor: 'pointer', fontFamily: 'inherit',
                                                                    transition: 'opacity 0.12s',
                                                                }}
                                                            >Yes</button>
                                                            <button
                                                                onClick={() => setPendingDelete(null)}
                                                                style={{
                                                                    fontSize: 11, fontWeight: 500, padding: '3px 10px',
                                                                    borderRadius: 5, border: '1px solid var(--border2)',
                                                                    background: 'var(--surface)', color: 'var(--muted)',
                                                                    cursor: 'pointer', fontFamily: 'inherit',
                                                                }}
                                                            >No</button>
                                                        </div>
                                                    ) : (
                                                        <div className={`rbx-tmpl-custom${activeTemplate?.id === t.id ? ' active' : ''}`}>
                                                            <button className="rbx-tmpl-custom-label" onClick={() => applyTemplate(t)}>{t.label}</button>
                                                            <button className="rbx-tmpl-custom-del" onClick={() => setPendingDelete(t.id)} title="Delete template">x</button>
                                                        </div>
                                                    )}
                                                </motion.div>
                                            ))}
                                        </AnimatePresence>
                                    </div>

                                    {/* Save current layout */}
                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                        <input
                                            ref={inputRef}
                                            className="rbx-save-input"
                                            type="text"
                                            placeholder="Save current layout..."
                                            value={saveName}
                                            onChange={(e) => setSaveName(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') saveTemplate(); }}
                                            maxLength={32}
                                        />
                                        <button className="rbx-save-btn" onClick={saveTemplate} disabled={!saveName.trim()}>Save</button>
                                    </div>
                                </div>
                            )}

                            {/* Sections, one block per page section. Section title
                                + per-widget switch rows. Title doubles as a "show
                                everything in this section" affordance. */}
                            {sections.map((section, si) => {
                                const sectionWidgetCount = section.widgets.length;
                                const onCount = section.widgets.filter((w) => !hidden.includes(w.id)).length;
                                return (
                                    <Box key={section.id} sx={{ mb: si < sections.length - 1 ? 2 : 0 }}>
                                        <Box
                                            onClick={() => toggleSection(section)}
                                            sx={{
                                                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                                                gap: 1,
                                                px: 1.25, py: 0.5, mb: 0.5,
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                transition: 'background 120ms',
                                                '&:hover': { bgcolor: 'action.hover' },
                                            }}
                                            title={onCount === sectionWidgetCount ? 'Hide entire section' : 'Show entire section'}
                                        >
                                            <Typography sx={{
                                                fontSize: 10, fontWeight: 700,
                                                textTransform: 'uppercase', letterSpacing: '0.07em',
                                                color: 'text.primary',
                                            }}>
                                                {section.title}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: 9, fontWeight: 600,
                                                color: onCount === 0 ? 'text.disabled' : 'text.secondary',
                                                letterSpacing: '0.04em',
                                            }}>
                                                {onCount}/{sectionWidgetCount}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                            {section.widgets.map((w) => (
                                                <WidgetRow
                                                    key={w.id}
                                                    widget={w}
                                                    isVisible={!hidden.includes(w.id)}
                                                    onToggle={onToggle}
                                                    onInfoOpen={handleInfoOpen}
                                                    secondary={secondary}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </div>

                        {/* Description popover */}
                        <Popover
                            id={infoPopover ? `rbx-info-${infoPopover.widget.id}` : undefined}
                            open={!!infoPopover}
                            anchorEl={infoPopover?.anchorEl}
                            onClose={handleInfoClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            disableRestoreFocus
                            PaperProps={{
                                sx: {
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
                                    p: 1.5,
                                    maxWidth: 240,
                                },
                            }}
                        >
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                                {infoPopover?.widget.label}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.55 }}>
                                {infoPopover?.widget.description}
                            </Typography>
                        </Popover>

                        {/* Footer */}
                        <div style={{
                            display: 'flex', gap: 6, padding: '12px 24px 16px',
                            flexShrink: 0,
                        }}>
                            <button
                                onClick={onRestoreAll}
                                disabled={hidden.length === 0}
                                style={{
                                    flex: 1, fontSize: 11, fontWeight: 500, padding: '7px 0',
                                    borderRadius: 7, border: '1px solid var(--border2)',
                                    background: 'transparent', color: hidden.length === 0 ? 'var(--dim)' : 'var(--muted)',
                                    cursor: hidden.length === 0 ? 'not-allowed' : 'pointer',
                                    fontFamily: 'inherit', transition: 'all 0.12s',
                                }}
                                onMouseEnter={(e) => { if (hidden.length > 0) { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.borderColor = 'var(--dim)'; } }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = hidden.length === 0 ? 'var(--dim)' : 'var(--muted)'; e.currentTarget.style.borderColor = 'var(--border2)'; }}
                            >
                                Reset
                            </button>
                            <button
                                onClick={onClose}
                                style={{
                                    flex: 1, fontSize: 11, fontWeight: 600, padding: '7px 0',
                                    borderRadius: 7, border: 'none',
                                    background: 'var(--teal)', color: '#fff',
                                    cursor: 'pointer', fontFamily: 'inherit', transition: 'filter 0.12s',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(0.92)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
                            >
                                Done
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
